import { inject, type InjectionKey } from "vue";
import { ColourPalette } from "@esenda/core-ui/constants";
import {
    getContrastRatio,
    getWalletPaymentButtonColour,
    type CheckoutTheme,
    type CheckoutThemeFontFamily,
    type CheckoutThemeParseResult,
} from "@/theme/checkoutTheme";

export const DEFAULT_THEME_NAME = "lightTheme";
export const CHECKOUT_THEME_NAME = "checkoutTheme";

/** Applied to .main-content when a theme is active; carries the theme CSS variables. */
export const THEME_SCOPE_CLASS = "checkout-theme-scope";

const STYLE_ELEMENT_ID = "checkout-theme";

/**
 * Generic keywords only. `monospace` is doubled on purpose: a lone `monospace`
 * makes browsers shrink the inherited font size (to 13px by default).
 */
const FONT_STACKS: Record<
    Exclude<CheckoutThemeFontFamily, "default">,
    string
> = {
    "sans-serif": "sans-serif",
    serif: "serif",
    monospace: "monospace, monospace",
};

export type ResolvedCheckoutTheme = Readonly<{
    /** The validated options exactly as accepted by parseCheckoutTheme. */
    options: Readonly<CheckoutTheme>;
    /** Custom properties declared on the theme scope (.main-content). */
    scopeVars: Readonly<Record<string, string>>;
    /** Colour overrides for the Vuetify checkoutTheme. */
    vuetifyColors: Readonly<Record<string, string>>;
    /** v-main background, or null to keep the default. */
    pageBackground: string | null;
    /** Font stack for the theme scope, or null to keep the default. */
    fontStack: string | null;
}>;

const MIN_TEXT_CONTRAST = 4.5;

/** Mixes two #rrggbb colours. `weight` is the share of `from`, 0 to 1. */
function mixHex(from: string, to: string, weight: number): string {
    const channel = (hex: string, index: number) =>
        parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16);

    return (
        "#" +
        [0, 1, 2]
            .map((index) =>
                Math.round(
                    channel(from, index) * weight +
                        channel(to, index) * (1 - weight),
                )
                    .toString(16)
                    .padStart(2, "0"),
            )
            .join("")
    );
}

const meetsContrast = (colour: string, surfaces: readonly string[]): boolean =>
    surfaces.every(
        (surface) => getContrastRatio(colour, surface) >= MIN_TEXT_CONTRAST,
    );

/** Resolution of the dimming search (2.5% increments). */
const SECONDARY_MIX_STEPS = 40;

/** Neutral greys from black to white, used when the theme's own text can't be made to pass. */
const NEUTRAL_STEPS = 20;
const BLACK = "#000000";

/**
 * Secondary text colour, or null to keep core-ui's defaults.
 *
 * Text sits on the page background (tiles are transparent) and placeholders
 * sit on the field background, so the result must clear 4.5:1 on both.
 *
 * Picks the dimmest mix of the primary text towards the page background that
 * passes on both surfaces ("dimmest" = lowest contrast against the page while
 * still passing). If no mix passes — e.g. the primary text itself fails, or
 * the page and field surfaces sit at opposite luminance extremes — falls back
 * to whichever of the primary text or a neutral grey is most legible across
 * both surfaces.
 */
function resolveSecondaryText(options: Readonly<CheckoutTheme>): string | null {
    const pageSurface = options.backgroundColor ?? ColourPalette.WHITE;
    const fieldSurface = options.fieldBackgroundColor ?? ColourPalette.WHITE;
    const surfaces = [pageSurface, fieldSurface];

    if (!options.textColor) {
        const surfacesChanged =
            options.backgroundColor !== undefined ||
            options.fieldBackgroundColor !== undefined;
        if (
            !surfacesChanged ||
            meetsContrast(ColourPalette.SLATE_GREY, surfaces)
        ) {
            return null;
        }
    }

    const baseText = options.textColor ?? ColourPalette.INK;

    let dimmest: string | null = null;
    let dimmestPageContrast = Number.POSITIVE_INFINITY;

    for (let step = 0; step <= SECONDARY_MIX_STEPS; step++) {
        const candidate = mixHex(
            baseText,
            pageSurface,
            step / SECONDARY_MIX_STEPS,
        );
        if (!meetsContrast(candidate, surfaces)) {
            continue;
        }

        const pageContrast = getContrastRatio(candidate, pageSurface);
        if (pageContrast < dimmestPageContrast) {
            dimmest = candidate;
            dimmestPageContrast = pageContrast;
        }
    }

    if (dimmest) {
        return dimmest;
    }

    return mostLegible(baseText, surfaces);
}

/**
 * The candidate with the best worst-case contrast across all surfaces.
 * Tries the theme's own text first, then a ramp of neutral greys, so a
 * dark page with a light field still lands on a mid grey that works on both
 * where one exists (black/white surfaces bottom out around 4.58:1).
 */
function mostLegible(baseText: string, surfaces: readonly string[]): string {
    const candidates = [baseText];
    for (let step = 0; step <= NEUTRAL_STEPS; step++) {
        candidates.push(
            mixHex(BLACK, ColourPalette.WHITE, step / NEUTRAL_STEPS),
        );
    }

    let best = baseText;
    let bestScore = Number.NEGATIVE_INFINITY;

    for (const candidate of candidates) {
        const score = Math.min(
            ...surfaces.map((surface) => getContrastRatio(candidate, surface)),
        );
        if (score > bestScore) {
            best = candidate;
            bestScore = score;
        }
    }

    if (import.meta.env.DEV && !meetsContrast(best, surfaces)) {
        console.warn(
            `[checkoutTheme] No secondary text colour clears 4.5:1 on ${surfaces.join(" and ")}; ` +
                `using ${best} (${bestScore.toFixed(2)}:1).`,
        );
    }

    return best;
}

/**
 * Error text colour, or null to keep core-ui's red.
 *
 * Error messages sit on the page background and the error icon sits on the
 * field background, so the result must clear 4.5:1 on both. Keeps the default
 * red when it already passes; otherwise picks the reddest mix towards white or
 * black (whichever contrasts more with the page) that does.
 */
function resolveErrorText(options: Readonly<CheckoutTheme>): string | null {
    const pageSurface = options.backgroundColor ?? ColourPalette.WHITE;
    const fieldSurface = options.fieldBackgroundColor ?? ColourPalette.WHITE;
    const surfaces = [pageSurface, fieldSurface];

    if (meetsContrast(ColourPalette.RED_500, surfaces)) {
        return null;
    }

    const towards =
        getContrastRatio("#ffffff", pageSurface) >=
        getContrastRatio("#000000", pageSurface)
            ? "#ffffff"
            : "#000000";

    for (let step = 19; step >= 1; step--) {
        const candidate = mixHex(ColourPalette.RED_500, towards, step / 20);
        if (meetsContrast(candidate, surfaces)) {
            return candidate;
        }
    }

    // textColor is validated against both surfaces, so it always passes.
    return options.textColor ?? towards;
}

/**
 * Turns a parse result into the values every consumer uses (CSS and Vuetify).
 * Returns null when there is nothing to apply, so rendering matches production.
 */
export function resolveCheckoutTheme(
    result: CheckoutThemeParseResult,
): ResolvedCheckoutTheme | null {
    if (result.status !== "valid") {
        return null;
    }

    const options = result.theme;
    const scopeVars: Record<string, string> = {};
    const vuetifyColors: Record<string, string> = {};

    if (options.primaryColor) {
        scopeVars["--action-primary"] = options.primaryColor;
        scopeVars["--action-primary-subtle"] =
            `color-mix(in srgb, ${options.primaryColor} 10%, white)`;
        scopeVars["--action-primary-subtle-border"] =
            `color-mix(in srgb, ${options.primaryColor} 20%, transparent)`;
        scopeVars["--adyen-sdk-color-label-highlight"] = options.primaryColor;
        scopeVars["--adyen-sdk-color-background-always-dark"] =
            options.primaryColor;
        // Focus outline follows primaryColor, matching the app's other focus styles.
        // The validator guarantees 3:1 against the field background.
        scopeVars["--adyen-sdk-color-outline-primary-active"] =
            options.primaryColor;

        vuetifyColors.primary = options.primaryColor;
    }

    if (options.primaryColor || options.primaryTextColor) {
        // Text on primary-coloured actions. When only primaryColor is set this is white,
        // the default the validator checked at 4.5:1 against primaryColor.
        const onPrimary = options.primaryTextColor ?? ColourPalette.WHITE;

        scopeVars["--action-on-primary"] = onPrimary;
        scopeVars["--adyen-sdk-color-label-on-color"] = onPrimary;
        vuetifyColors["on-primary"] = onPrimary;
    }

    if (options.textColor) {
        scopeVars["--text-primary"] = options.textColor;
        scopeVars["--adyen-sdk-color-label-primary"] = options.textColor;
        vuetifyColors["on-background"] = options.textColor;
    }

    const secondaryText = resolveSecondaryText(options);
    if (secondaryText) {
        scopeVars["--text-secondary"] = secondaryText;
        scopeVars["--text-label"] = secondaryText;
        // Core-ui aliases these at :root, so they must be re-declared inside the scope.
        scopeVars["--adyen-sdk-color-label-secondary"] = secondaryText;
        scopeVars["--adyen-sdk-color-label-tertiary"] = secondaryText;
        scopeVars["--adyen-sdk-color-label-disabled"] = secondaryText;
    }

    const errorText = resolveErrorText(options);
    if (errorText) {
        scopeVars["--status-error"] = errorText;
        // Core-ui aliases these at :root, so they must be re-declared inside the scope.
        scopeVars["--adyen-sdk-color-label-critical"] = errorText;
        scopeVars["--adyen-sdk-color-outline-critical"] = errorText;
    }

    if (options.fieldBackgroundColor) {
        scopeVars["--checkout-field-background"] = options.fieldBackgroundColor;
        scopeVars["--adyen-sdk-color-background-primary"] =
            options.fieldBackgroundColor;
    }

    if (options.borderColor) {
        scopeVars["--border-default"] = options.borderColor;
        scopeVars["--adyen-sdk-color-outline-primary"] = options.borderColor;
        scopeVars["--adyen-sdk-color-outline-primary-hover"] =
            options.borderColor;
        scopeVars["--adyen-sdk-color-outline-secondary"] = options.borderColor;
        scopeVars["--adyen-sdk-color-outline-disabled"] = options.borderColor;
        scopeVars["--adyen-sdk-color-separator-primary"] = options.borderColor;
    }

    let fontStack: string | null = null;
    if (options.fontFamily && options.fontFamily !== "default") {
        fontStack = FONT_STACKS[options.fontFamily];
        scopeVars["--checkout-font-family"] = fontStack;
        scopeVars["--font-family"] = fontStack;
        scopeVars["--dp-font-family"] = fontStack;
    }

    if (options.buttonRadius !== undefined) {
        scopeVars["--radius-button"] = `${options.buttonRadius}px`;
    }

    if (options.inputRadius !== undefined) {
        scopeVars["--radius-input"] = `${options.inputRadius}px`;
        scopeVars["--adyen-sdk-border-radius-m"] = `${options.inputRadius}px`;
    }

    if (options.contentPadding !== undefined) {
        scopeVars["--checkout-content-padding"] = `${options.contentPadding}px`;
    }

    const pageBackground = options.backgroundColor ?? null;

    if (
        Object.keys(scopeVars).length === 0 &&
        Object.keys(vuetifyColors).length === 0 &&
        pageBackground === null
    ) {
        return null;
    }

    return Object.freeze({
        options,
        scopeVars: Object.freeze(scopeVars),
        vuetifyColors: Object.freeze(vuetifyColors),
        pageBackground,
        fontStack,
    });
}

/** Native inputs that use the app's generic field styling (Adyen styles its own). */
const NATIVE_FIELD_SELECTORS = [
    'input[type="text"]:not(.adyen-checkout__input):not(:disabled)',
    'input[type="email"]:not(.adyen-checkout__input):not(:disabled)',
    'input[type="number"]:not(.adyen-checkout__input):not(:disabled)',
];

function buildStyleSheet(theme: ResolvedCheckoutTheme): string {
    const rules: string[] = [];

    const declarations = Object.entries(theme.scopeVars).map(
        ([name, value]) => `    ${name}: ${value};`,
    );
    if (theme.fontStack) {
        declarations.push("    font-family: var(--checkout-font-family);");
    }
    if (declarations.length > 0) {
        // Also declared on the checkout theme class so teleported overlays
        // (select / autocomplete menus render outside the scope element)
        // inherit the same variables and font.
        rules.push(
            `.${THEME_SCOPE_CLASS},\n.v-theme--${CHECKOUT_THEME_NAME} {\n${declarations.join("\n")}\n}`,
        );
    }

    if (theme.pageBackground) {
        // Declared on every element carrying the checkout theme class: the v-app
        // (inherited down to .v-main, where _vuetify.css reads it) and teleported
        // overlays such as select / autocomplete menus, which render outside v-app.
        rules.push(
            `.v-theme--${CHECKOUT_THEME_NAME} {\n    --checkout-page-background: ${theme.pageBackground};\n}`,
        );
    }

    if (theme.options.fieldBackgroundColor) {
        // Only emitted when the option is set, so unthemed fields keep their existing backgrounds.
        // Disabled fields are excluded and keep their disabled styling.
        const selectors = [
            `.${THEME_SCOPE_CLASS} .v-field--variant-outlined:not(.v-field--disabled)`,
            ...NATIVE_FIELD_SELECTORS.map(
                (selector) => `.${THEME_SCOPE_CLASS} ${selector}`,
            ),
        ];
        rules.push(
            `${selectors.join(",\n")} {\n    background-color: var(--checkout-field-background);\n}`,
        );

        // Dropdown menus match the field they open from. Text uses the theme text
        // colour, which validation requires to reach 4.5:1 against the field background.
        // Background and colour transitions are dropped so menus don't fade from surface white.
        const menuSelectors = [
            ".v-select__content",
            ".v-autocomplete__content",
            ".v-combobox__content",
            ".v-select__content .v-list",
            ".v-autocomplete__content .v-list",
            ".v-combobox__content .v-list",
        ].map(
            (selector) =>
                `.v-theme--${CHECKOUT_THEME_NAME}${selector}, .v-theme--${CHECKOUT_THEME_NAME} ${selector}`,
        );
        rules.push(
            `${menuSelectors.join(",\n")} {\n    background: var(--checkout-field-background);\n    color: var(--checkout-text-color, inherit);\n}`,
        );
    }

    return rules.join("\n\n");
}
/**
 * Injects the theme stylesheet into <head>. Call before app.mount() so the
 * first paint is already themed. All values are validated hex, integer px or
 * enum-mapped, so nothing partner-supplied reaches the CSS unvalidated.
 */
export function mountCheckoutThemeStyles(
    theme: ResolvedCheckoutTheme | null,
): void {
    if (!theme) {
        return;
    }

    const css = buildStyleSheet(theme);
    if (!css) {
        return;
    }

    document.getElementById(STYLE_ELEMENT_ID)?.remove();

    const style = document.createElement("style");
    style.id = STYLE_ELEMENT_ID;
    style.textContent = css;
    document.head.appendChild(style);
}

export const CheckoutThemeKey: InjectionKey<ResolvedCheckoutTheme | null> =
    Symbol("checkoutTheme");

/** Read-only access to the resolved theme. null means default styling. */
export function useCheckoutTheme(): ResolvedCheckoutTheme | null {
    return inject(CheckoutThemeKey, null);
}

/**
 * Resolves the wallet button colour from the provided checkout theme.
 * Must be called synchronously inside setup() or another composable.
 */
export const useWalletPaymentButtonColour = (): "white" | "black" =>
    getWalletPaymentButtonColour(
        inject(CheckoutThemeKey, null)?.pageBackground,
    );
